import React, { useRef, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { ChatMessage as ChatMessageType } from '../../types/chat';
import { AVAILABLE_ACTIONS, CHAT_TEXT } from '../../constants/chat';
import { createUserMessage, createAssistantMessage } from '../../utils/chatUtils';

export interface IcpContext {
  id: string;
  name: string;
  solution_products?: string;
  customer_type?: string;
  selling_motion?: string;
  company_sizes?: string[];
  target_verticals?: string[];
  titles?: string;
  target_region?: string[];
}

interface TableContext {
  name?: string;
  type?: 'companies' | 'people' | 'custom';
  columns?: { key: string; name: string; isSegment?: boolean }[];
  [key: string]: any;
}

interface UseIcpAutoTriggerProps {
  icpContext?: IcpContext | null;
  tableContext?: TableContext;
  tableId?: string;
  sessionId: string | null;
  setMessages: React.Dispatch<React.SetStateAction<ChatMessageType[]>>;
  setSessionId: (id: string | null) => void;
  setIsLoading: (loading: boolean) => void;
}

/**
 * Hook for auto-triggering dataset generation when ICP context is set
 * Builds a detailed prompt from ICP details and sends it automatically
 */
export function useIcpAutoTrigger({
  icpContext,
  tableContext,
  tableId,
  sessionId,
  setMessages,
  setSessionId,
  setIsLoading,
}: UseIcpAutoTriggerProps): void {
  // Track processed ICP to avoid duplicate triggers
  const processedIcpRef = useRef<string | null>(null);
  // Track current ICP to detect stale responses
  const currentIcpRef = useRef<string | null>(null);
  // Track if there's an active request in flight
  const hasActiveRequestRef = useRef<boolean>(false);

  // Always update the current ICP ref when icpContext changes
  // Also clear loading if ICP changes while a request is in flight (prevents stuck spinner)
  useEffect(() => {
    const previousIcp = currentIcpRef.current;
    const newIcp = icpContext?.id || null;
    currentIcpRef.current = newIcp;

    // If ICP changed and there's an active request, clear loading since the request is now stale
    if (previousIcp !== null && previousIcp !== newIcp && hasActiveRequestRef.current) {
      setIsLoading(false);
    }
  }, [icpContext?.id, setIsLoading]);

  useEffect(() => {
    if (icpContext && icpContext.name && icpContext.id !== processedIcpRef.current) {
      // Mark this ICP as processed to avoid duplicate triggers
      processedIcpRef.current = icpContext.id;
      // Capture the ICP ID for staleness check
      const requestIcpId = icpContext.id;

      // Build a detailed prompt with ICP context
      const icpDetails: string[] = [];
      if (icpContext.target_verticals?.length) {
        icpDetails.push(`industries: ${icpContext.target_verticals.join(', ')}`);
      }
      if (icpContext.target_region?.length) {
        icpDetails.push(`regions: ${icpContext.target_region.join(', ')}`);
      }
      if (icpContext.company_sizes?.length) {
        icpDetails.push(`company sizes: ${icpContext.company_sizes.join(', ')}`);
      }
      if (icpContext.titles) {
        icpDetails.push(`targeting: ${icpContext.titles}`);
      }

      const detailsStr = icpDetails.length > 0 ? ` (${icpDetails.join('; ')})` : '';
      const prompt = `Find leads for my "${icpContext.name}" ICP${detailsStr}`;

      // Create and send the message automatically
      const userMessage = createUserMessage(prompt, 'icp-user');

      setMessages(prev => [...prev, userMessage]);
      setIsLoading(true);
      hasActiveRequestRef.current = true;

      // Send to API
      (async () => {
        try {
          // Enhanced context with available actions
          const enhancedContext = {
            ...tableContext,
            availableActions: tableId
              ? AVAILABLE_ACTIONS.TABLE_ACTIONS
              : AVAILABLE_ACTIONS.DATASET_ACTIONS
          };

          const response = await apiClient.sendChatMessage(prompt, enhancedContext, [], sessionId);

          // Check if user switched to a different ICP while waiting for response
          if (currentIcpRef.current !== requestIcpId) {
            // Stale response - discard it
            return;
          }

          if (response.session_id && !sessionId) {
            setSessionId(response.session_id);
          }

          const assistantMessage = createAssistantMessage(response.content, {
            idPrefix: 'icp-assistant',
            actions: response.actions,
          });

          setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
          // Check for staleness before showing error
          if (currentIcpRef.current !== requestIcpId) {
            return;
          }
          console.error('Error sending ICP message:', error);
          const errorMessage = createAssistantMessage(CHAT_TEXT.ERROR_MESSAGE, {
            idPrefix: 'icp-error',
          });
          setMessages(prev => [...prev, errorMessage]);
        } finally {
          // Only clear the active request flag if this is still the current ICP or ICP was cleared
          // If ICP changed to a different one, the newer request will clear this when it completes
          if (currentIcpRef.current === requestIcpId || currentIcpRef.current === null) {
            hasActiveRequestRef.current = false;
          }
          // Only clear loading if this is still the current ICP
          if (currentIcpRef.current === requestIcpId) {
            setIsLoading(false);
          }
        }
      })();
    }
  }, [icpContext, tableContext, sessionId, tableId, setMessages, setSessionId, setIsLoading]);
}
